import { useMemo, useState } from 'react';
import { cx } from '../lib/cx';
import { Avatar } from './Avatar';
import { Input } from './Input';

export interface ParticipantOption {
  id: string;
  name: string;
  /** Sekundarni redak; obično e-mail, da se dva Ivana razlikuju. */
  hint?: string | null;
}

export interface ParticipantPickerLabels {
  search: string;
  selected: string;
  all: string;
  none: string;
  empty: string;
  noMatch: string;
}

export interface ParticipantPickerProps {
  id: string;
  options: readonly ParticipantOption[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
  labels: ParticipantPickerLabels;
  error?: string | undefined;
  /** Iznad ovog broja popis dobiva vlastito klizanje umjesto da razvuče modal. */
  maxVisible?: number;
}

/**
 * Odabir sudionika aktivnosti. Kvačice, a ne `multiple` select — mišem se u
 * `<select multiple>` bez Ctrl gubi cijeli odabir, a trener ovdje bira šest ljudi.
 *
 * Pretraga se pojavljuje tek kad popis preraste ekran, da uobičajenih pet
 * klijenata ne dobije prazno polje iznad sebe.
 */
export function ParticipantPicker({
  id,
  options,
  value,
  onChange,
  labels,
  error,
  maxVisible = 8,
}: ParticipantPickerProps) {
  const [query, setQuery] = useState('');
  const odabrani = useMemo(() => new Set(value), [value]);

  const vidljivi = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('hr');
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name.toLocaleLowerCase('hr').includes(q) ||
        (o.hint ?? '').toLocaleLowerCase('hr').includes(q),
    );
  }, [options, query]);

  function toggle(optionId: string): void {
    onChange(
      odabrani.has(optionId) ? value.filter((v) => v !== optionId) : [...value, optionId],
    );
  }

  if (options.length === 0) {
    return <p className="text-[13.5px] text-ink-muted">{labels.empty}</p>;
  }

  const sviVidljiviOdabrani = vidljivi.length > 0 && vidljivi.every((o) => odabrani.has(o.id));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium text-ink">
          {labels.selected.replace('{n}', String(value.length))}
        </span>
        <button
          type="button"
          className="text-[12.5px] text-accent underline-offset-2 hover:underline"
          onClick={() =>
            onChange(
              sviVidljiviOdabrani
                ? value.filter((v) => !vidljivi.some((o) => o.id === v))
                : [...new Set([...value, ...vidljivi.map((o) => o.id)])],
            )
          }
        >
          {sviVidljiviOdabrani ? labels.none : labels.all}
        </button>
      </div>

      {options.length > maxVisible && (
        <Input
          id={`${id}-search`}
          type="search"
          aria-label={labels.search}
          placeholder={labels.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      <ul
        aria-label={labels.selected.replace('{n}', String(value.length))}
        className={cx(
          'flex flex-col divide-y divide-line-subtle overflow-y-auto rounded-md border',
          error ? 'border-danger' : 'border-line-subtle',
          options.length > maxVisible && 'max-h-64',
        )}
      >
        {vidljivi.length === 0 && (
          <li className="px-3 py-4 text-[13.5px] text-ink-muted">{labels.noMatch}</li>
        )}
        {vidljivi.map((o) => {
          const checked = odabrani.has(o.id);
          return (
            <li key={o.id}>
              <label
                htmlFor={`${id}-${o.id}`}
                className={cx(
                  'flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors',
                  checked ? 'bg-accent-light/40' : 'hover:bg-bg-alt',
                )}
              >
                <input
                  id={`${id}-${o.id}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.id)}
                  className="h-[18px] w-[18px] shrink-0 cursor-pointer rounded-[4px] border border-line-control accent-accent"
                />
                <Avatar name={o.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14.5px] text-ink">{o.name}</span>
                  {o.hint && (
                    <span className="block truncate text-[12px] text-ink-muted">{o.hint}</span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {error && (
        <p role="alert" className="text-[12px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
